import React, { useState, useEffect } from 'react';

const ScratchMatch = ({ match, isSuper = false, index = 0 }) => {
    const [scratched, setScratched] = useState(false);
    const [revealTriggered, setRevealTriggered] = useState(false);
    const canvasRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const isDrawing = React.useRef(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container || scratched) return;

        // Set canvas internal resolution to match container size
        const rect = container.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;

        const ctx = canvas.getContext('2d');
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        
        const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        if (isSuper) {
            grad.addColorStop(0, '#f59e0b');
            grad.addColorStop(0.5, '#fbbf24');
            grad.addColorStop(1, '#d97706');
        } else {
            grad.addColorStop(0, '#ec4899');
            grad.addColorStop(0.5, '#f472b6');
            grad.addColorStop(1, '#db2777');
        }
        
        ctx.fillStyle = grad;
        ctx.beginPath();
        const x = 0, y = 0, width = canvas.width, height = canvas.height, radius = 30;
        ctx.moveTo(x + radius, y); ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height); ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius); ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath(); ctx.fill();
        
        for (let i = 0; i < 60; i++) {
            ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.beginPath();
            ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, Math.random() * 1.5, 0, Math.PI * 2); ctx.fill();
        }
        
        ctx.fillStyle = 'white'; 
        const fontSize = Math.min(24, canvas.width / 15);
        ctx.font = `900 ${fontSize}px "Inter", sans-serif`; 
        ctx.textAlign = 'center';
        ctx.fillText('✨ GRATTA QUI ✨', canvas.width / 2, canvas.height / 2 + 8);
        
        const getPos = (e) => {
            const r = canvas.getBoundingClientRect();
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            return { 
                x: (clientX - r.left) * (canvas.width / r.width), 
                y: (clientY - r.top) * (canvas.height / r.height) 
            };
        };
        
        const scratch = (e) => { 
            if (!isDrawing.current) return; 
            const pos = getPos(e); 
            ctx.globalCompositeOperation = 'destination-out'; 
            ctx.beginPath(); 
            // Larger radius for mobile (min 35, scales with screen)
            const scratchRadius = Math.max(35, Math.min(50, canvas.width / 8));
            ctx.arc(pos.x, pos.y, scratchRadius, 0, Math.PI * 2); 
            ctx.fill(); 
        };
        
        const start = (e) => { isDrawing.current = true; scratch(e); };
        const end = () => { isDrawing.current = false; };
        
        canvas.addEventListener('mousedown', start); 
        canvas.addEventListener('mousemove', scratch); 
        canvas.addEventListener('mouseup', end);
        canvas.addEventListener('touchstart', start, { passive: false }); 
        canvas.addEventListener('touchmove', (e) => { e.preventDefault(); scratch(e); }, { passive: false });
        canvas.addEventListener('touchend', end);
        
        return () => {
            canvas.removeEventListener('mousedown', start); 
            canvas.removeEventListener('mousemove', scratch); 
            canvas.removeEventListener('mouseup', end);
            canvas.removeEventListener('touchstart', start); 
            canvas.removeEventListener('touchmove', scratch); 
            canvas.removeEventListener('touchend', end);
        };
    }, [isSuper, scratched]);

    const handleReveal = () => { setScratched(true); setRevealTriggered(true); };

    return (
        <div ref={containerRef} className={`position-relative mb-4 fade-in-up shadow-lg`} style={{ minHeight: '140px', animationDelay: `${index * 0.1}s`, borderRadius: '35px' }}>
            <div className={`glass-premium w-100 h-100 p-3 p-md-4 d-flex align-items-center rounded-5 border-2 ${isSuper ? 'border-warning' : 'border-white'} transition-all`}>
                <div className="flex-grow-1 pe-2 pe-md-3" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                        <h3 className="fw-900 mb-0 text-dark" style={{ fontSize: '1.2rem' }}>{match.firstName} {match.lastName}</h3>
                        {isSuper && <span className="badge bg-warning text-dark rounded-pill px-2 py-1 fw-800" style={{ fontSize: '0.6rem' }}>✨ MAGIC MATCH</span>}
                    </div>
                    <div className="d-flex flex-column gap-1 small fw-600 text-dark overflow-hidden">
                        <div className="text-truncate"><i className="fas fa-envelope me-2 text-pink-600" style={{ width: '14px' }}></i> {match.email}</div>
                        {match.phone && <div className="text-truncate"><i className="fas fa-phone me-2 text-pink-600" style={{ width: '14px' }}></i> {match.phone}</div>}
                        {match.instagram && <div className="text-truncate"><i className="fab fa-instagram me-2 text-pink-600" style={{ width: '14px' }}></i> @{match.instagram}</div>}
                    </div>
                </div>
                <div className={`d-flex align-items-center justify-content-center shadow-sm ${isSuper ? 'bg-warning text-dark' : 'bg-pink-600 text-white'}`} style={{ width: '60px', height: '60px', borderRadius: '20px', minWidth: '60px' }}>
                    <i className={`fas ${isSuper ? 'fa-star' : 'fa-heart'} fa-xl ${revealTriggered ? 'animate-heart-pop' : ''}`}></i>
                </div>
            </div>
            {!scratched && (
                <div className="position-absolute top-0 start-0 w-100 h-100 overflow-hidden rounded-5" style={{ zIndex: 10 }}>
                    <canvas ref={canvasRef} className="w-100 h-100" style={{ cursor: 'crosshair', touchAction: 'none' }} />
                    <button className="btn-reveal-manual position-absolute top-0 end-0 m-2 btn btn-xs btn-dark opacity-75 rounded-pill" style={{ fontSize: '0.6rem' }} onClick={handleReveal}>RIVELA <i className="fas fa-magic ms-1"></i></button>
                </div>
            )}
        </div>
    );
};

const ActiveGame = ({ participant, others, results, loading, noteDrafts, handleNoteChange, handleVote, handleVerify, handleLogout, onEditProfile, onShowMatches, shareLastName, sharePhone, privacyAccepted, onSavePreferences, preferencesSaved }) => {
    return (
        <div className="mx-auto w-100" style={{ maxWidth: '800px' }}>
            <div className="card glass-premium border-0 shadow-lg mb-4 fade-in-up mx-2 position-relative" style={{ borderRadius: '45px', overflow: 'visible' }}>
                <div className="p-4 p-md-5 text-white" style={{ background: 'linear-gradient(135deg, #db2777, #f43f5e)' }}>
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center align-items-sm-start gap-4">
                        <div className="d-flex align-items-center gap-3">
                            <div className="position-relative">
                                <div className="bg-white p-2 rounded-4 shadow-sm d-flex align-items-center justify-content-center overflow-hidden" style={{ width: '50px', height: '50px' }}>
                                    {participant.photo ? (
                                        <img src={participant.photo} alt="Me" className="w-100 h-100 object-fit-cover" />
                                    ) : (
                                        <i className="fas fa-heart" style={{ color: '#db2777' }}></i>
                                    )}
                                </div>
                                <button 
                                    className="btn btn-white shadow-sm rounded-circle position-absolute border-0 d-flex align-items-center justify-content-center" 
                                    onClick={onEditProfile} 
                                    style={{ bottom: '-5px', right: '-5px', width: '24px', height: '24px', zIndex: 5, padding: 0 }}
                                >
                                    <i className="fas fa-pencil-alt text-pink-600" style={{ fontSize: '0.6rem' }}></i>
                                </button>
                            </div>
                            <div>
                                <h5 className="text-white text-opacity-80 mb-1 small text-uppercase fw-900 ls-lg" style={{ fontSize: '0.6rem' }}>Solteros Speed Date</h5>
                                <h1 className="fw-900 mb-2" style={{ fontSize: '2rem', lineHeight: '1.2' }}>{participant.Event?.name}</h1>
                                <div className="d-flex gap-2">
                                    <button className="btn rounded-pill px-3 shadow-sm d-flex align-items-center gap-2 fw-900" onClick={() => handleVerify(participant.token)} style={{ fontSize: '0.7rem', height: '32px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
                                        <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                                        <span>AGGIORNA</span>
                                    </button>
                                    <button className="btn rounded-pill px-3 shadow-sm d-flex align-items-center gap-2 fw-900" onClick={handleLogout} style={{ fontSize: '0.7rem', height: '32px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
                                        <i className="fas fa-sign-out-alt"></i>
                                        <span>ESCI</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-body p-4 p-md-5">
                    <div className="d-flex align-items-center">
                        <div className="rounded-circle bg-white shadow-sm p-1 me-4">
                            <div
                                className={`rounded-circle bg-${participant.sex === 'male' ? 'primary' : 'danger'} bg-opacity-10 text-${participant.sex === 'male' ? 'primary' : 'danger'} d-flex align-items-center justify-content-center overflow-hidden`}
                                style={{ width: '80px', height: '80px' }}
                            >
                                {participant.photo ? (
                                    <img src={participant.photo} alt="Profile" className="w-100 h-100 object-fit-cover" />
                                ) : (
                                    <i className={`fas ${participant.sex === 'male' ? 'fa-mars' : 'fa-venus'} fa-2x`}></i>
                                )}
                            </div>
                        </div>
                        <div>
                            <h2 className="fw-900 mb-1 text-dark" style={{ fontSize: '1.5rem' }}>{participant.firstName} {participant.lastName}</h2>
                            <span className="badge bg-pink-100 text-pink-600 rounded-pill px-3 py-2 text-uppercase fw-800" style={{ fontSize: '0.65rem' }}>PARTECIPANTE <strong>#{participant.arrivalNumber}</strong></span>
                        </div>
                    </div>
                </div>
            </div>

            {participant.Event?.status < 4 ? (
                <div className="card glass-premium border-0 p-5 text-center fade-in-up" style={{ borderRadius: '45px' }}>
                    <div className="py-5">
                        <div className="spinner-grow text-pink-500 mb-4" style={{ width: '4rem', height: '4rem' }}></div>
                        <h1 className="fw-900 mb-2">Preparati allo Speed Date!</h1>
                        <p className="text-muted fw-600 mb-5">L'evento inizierà a breve. Rimani su questa pagina.</p>
                        <button className="btn btn-lg btn-danger rounded-pill px-5 py-3 fw-900 shadow-lg border-0" style={{ background: 'linear-gradient(45deg, #db2777, #f43f5e)' }} onClick={() => handleVerify(participant.token)}>AGGIORNA SUBITO</button>
                    </div>
                </div>
            ) : results && participant.MatchEmailSent && participant.Event?.status >= 6 ? (
                <div className="fade-in">
                    <div className="text-center mb-5"><h1 className="fw-900 text-dark mb-0" style={{ fontSize: '2.5rem' }}>I tuoi Match! ✨</h1><p className="text-muted fw-600">Gratta per scoprire chi ha ricambiato il tuo interesse.</p></div>
                    {results.matches.map((m, i) => <ScratchMatch key={i} match={m} index={i} />)}
                    {results.superMatches.map((m, i) => <ScratchMatch key={i} match={m} isSuper={true} index={i + 10} />)}
                    {(results.matches.length === 0 && results.superMatches.length === 0) && (
                        <div className="card glass-premium border-0 p-5 text-center shadow-lg" style={{ borderRadius: '45px' }}><i className="fas fa-heart-broken fa-3x text-muted mb-4 opacity-30"></i><h2 className="fw-900 text-dark mb-2">Nessun Match stavolta</h2><p className="text-muted fw-600 mb-0">Non scoraggiarti, il prossimo evento potrebbe essere quello giusto! ❤️</p></div>
                    )}
                </div>
            ) : (
                <div className="fade-in">
                    {participant.Event?.status >= 5 ? (
                        participant.Event?.status >= 6 ? (
                            <div className="card glass-premium border-0 p-5 text-center shadow-lg" style={{ borderRadius: '45px' }}>
                                <div className="py-4">
                                    <div className="mb-4" style={{ fontSize: '4rem' }}>📭</div>
                                    <h1 className="fw-900 text-dark mb-3" style={{ fontSize: '2rem' }}>Match in arrivo!</h1>
                                    <p className="text-muted fw-600 mb-0 fs-5">I risultati sono stati pubblicati ma non hai ancora match. Controlla la sezione eventi passati.</p>
                                </div>
                            </div>
                        ) : (
                            <div className="card glass-premium border-0 p-5 text-center shadow-lg" style={{ borderRadius: '45px' }}>
                                <div className="py-4">
                                    <div className="mb-4" style={{ fontSize: '4rem' }}>⏳</div>
                                    <h1 className="fw-900 text-dark mb-3" style={{ fontSize: '2rem' }}>Gioco Terminato!</h1>
                                    <p className="text-muted fw-600 mb-0 fs-5">Il gioco è terminato. Riceverai i tuoi match via email!</p>
                                </div>
                            </div>
                        )
                    ) : (
                    <>
                    <div className="text-center mb-5">
                        <h1 className="fw-900 text-dark mb-0" style={{ fontSize: '2.5rem' }}>Vota i tuoi partecipanti preferiti!</h1>
                        <p className="text-muted fw-600">Esprimi le tue preferenze per le persone che hai conosciuto.</p>
                    </div>
                    <div className="row g-4">
                        {others.map(other => {
                            const vote = other.ReceivedVotes?.[0]; const currentChoice = vote?.choice; const currentMagic = vote?.isMagic;
                            return (
                                <div className="col-12" key={other.id}>
                                    <div className={`card glass-premium border-0 p-4 transition-all rounded-5 ${currentMagic ? 'border-warning border-start border-4' : ''}`} style={{ border: '1px solid rgba(219,39,119,0.1)' }}>
                                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                            <div className="d-flex align-items-center">
                                                <div className="position-relative me-3">
                                                    <div
                                                        className="bg-white shadow-sm rounded-circle overflow-hidden d-flex align-items-center justify-content-center"
                                                        style={{ width: '70px', height: '70px', border: '2px solid white' }}
                                                    >
                                                        {other.photo ? (
                                                            <img src={other.photo} alt={other.firstName} className="w-100 h-100 object-fit-cover" />
                                                        ) : (
                                                            <div className="text-pink-600 fw-900" style={{ fontSize: '1.5rem' }}>{other.arrivalNumber}</div>
                                                        )}
                                                    </div>
                                                    {other.photo && (
                                                        <div
                                                            className="position-absolute bottom-0 end-0 bg-white shadow-sm rounded-circle d-flex align-items-center justify-content-center fw-900 text-pink-600 border border-light"
                                                            style={{ width: '25px', height: '25px', fontSize: '0.7rem' }}
                                                        >
                                                            {other.arrivalNumber}
                                                        </div>
                                                    )}
                                                </div>
                                                <div><h4 className="fw-900 mb-0 text-dark">{participant.Event?.status >= 5 ? `Partecipante #${other.arrivalNumber}` : other.firstName}</h4><small className="text-muted fw-700">{other.birthDate ? `${new Date().getFullYear() - new Date(other.birthDate).getFullYear()} ANNI` : ''}</small></div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className={`btn btn-sm rounded-pill px-4 fw-900 ${currentChoice === true ? 'btn-success shadow-lg' : 'btn-outline-success opacity-50 border-2'}`}
                                                    onClick={() => handleVote(other.id, true, currentMagic)}
                                                    disabled={participant.Event?.status >= 5}
                                                >SI</button>
                                                <button
                                                    className={`btn btn-sm rounded-pill px-4 fw-900 ${currentChoice === false ? 'btn-danger shadow-lg' : 'btn-outline-danger opacity-50 border-2'}`}
                                                    onClick={() => handleVote(other.id, false, false)}
                                                    disabled={participant.Event?.status >= 5}
                                                >NO</button>
                                                <button
                                                    className={`btn btn-sm rounded-pill px-4 fw-900 ${currentMagic ? 'btn-warning shadow-lg' : 'btn-outline-warning opacity-50 border-2'}`}
                                                    onClick={() => handleVote(other.id, true, !currentMagic)}
                                                    disabled={participant.Event?.status >= 5}
                                                ><i className="fas fa-star me-2"></i> MAGIC</button>
                                            </div>
                                        </div>
                                        <div className="mt-4"><div className="input-group input-group-lg"><span className="input-group-text bg-white border-0 text-muted ps-4"><i className="fas fa-pencil-alt"></i></span><input type="text" className="form-control border-0 bg-white ps-2 py-3 fw-600" placeholder={participant.Event?.status >= 5 ? "Votazioni chiuse" : "Aggiungi una nota privata..."} value={noteDrafts[other.id] || ''} onChange={(e) => handleNoteChange(other.id, e.target.value)} disabled={participant.Event?.status >= 5} style={{ fontSize: '0.9rem', borderRadius: '0 20px 20px 0' }} /></div></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {participant.Event?.status < 5 && (
                    <div className="mt-4 pt-4 border-top mb-4">
                        <h5 className="fw-900 text-dark mb-3">Condivisione dati personali</h5>
                        <p className="text-muted small mb-3">Scegli quali dati condividere con i partecipanti che hai votato, in caso di abbinamento per questo evento:</p>
                        
                        <div className="d-flex align-items-center justify-content-between mb-3 p-3 bg-light rounded-4">
                            <div>
                                <span className="fw-900 text-dark">Condividi il cognome con gli eventuali abbinamenti</span>
                                <p className="text-muted small mb-0">Il tuo cognome sarà visibile ai contatti che hai votato in caso di abbinamento</p>
                            </div>
                            <div className="form-check form-switch d-inline-block ms-auto mb-0">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    role="switch"
                                    checked={shareLastName}
                                    onChange={(e) => onSavePreferences && onSavePreferences({ shareLastName: e.target.checked })}
                                />
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between mb-3 p-3 bg-light rounded-4">
                            <div>
                                <span className="fw-900 text-dark">Condividi il numero di cellulare con gli eventuali abbinamenti</span>
                                <p className="text-muted small mb-0">Il tuo numero di telefono sarà visibile ai contatti che hai votato in caso di abbinamento</p>
                            </div>
                            <div className="form-check form-switch d-inline-block ms-auto mb-0">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    role="switch"
                                    checked={sharePhone}
                                    onChange={(e) => onSavePreferences && onSavePreferences({ sharePhone: e.target.checked })}
                                />
                            </div>
                        </div>

                        {!preferencesSaved && (
                        <div className="p-3 bg-light rounded-4 mb-3">
                            <div className="form-check mb-0">
                                <label className="form-check-label" style={{fontSize:'12px',color:'#6b7280',lineHeight:'1.5',cursor:'pointer'}}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={privacyAccepted}
                                        onChange={(e) => onSavePreferences && onSavePreferences({ privacyAccepted: e.target.checked })}
                                        required
                                        style={{marginTop:'3px',accentColor:'#db2777'}}
                                    />
                                    I dati personali saranno utilizzati esclusivamente per le finalità degli eventi e non per finalità di marketing. La tua email potrà essere utilizzata esclusivamente per le finalità del gioco relative all'evento, e sarà comunicata solo con i partecipanti che hai votato.
                                    (<a href="https://www.iubenda.com/privacy-policy/14519431" target="_blank" rel="noopener noreferrer" style={{color:'#db2777'}}>Privacy Policy</a>)
                                </label>
                            </div>
                        </div>
                        )}

                        {!preferencesSaved ? (
                            <button
                                className="btn btn-danger w-100 rounded-pill py-3 mt-2 fw-900 shadow-lg border-0"
                                style={{ background: 'linear-gradient(45deg, #db2777, #f43f5e)' }}
                                disabled={!privacyAccepted}
                                onClick={() => onSavePreferences && onSavePreferences({ save: true })}
                            >
                                <i className="fas fa-check-circle me-2"></i>Salva preferenze
                            </button>
                        ) : (
                            <div className="alert alert-success border-0 rounded-4 p-3 mb-0 text-center fw-700">
                                <i className="fas fa-check-circle me-2"></i>Preferenze salvate con successo
                            </div>
                        )}
                    </div>
                    )}
                    </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ActiveGame;
